// Vendor Libs
import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';

// Dish Tech React
import {useForm, useToast} from '@dishtech-react/hooks';
import {InputTextField} from '@dishtech-react/core';

// Material UI
import CancelIcon from '@material-ui/icons/Cancel';
import SaveIcon from '@material-ui/icons/Save';
import {Button, IconButton, InputAdornment, Tooltip} from '@material-ui/core';
import {makeStyles} from '@material-ui/core/styles';
import ChipInput from 'material-ui-chip-input';
import RemoveRedEye from '@material-ui/icons/RemoveRedEye';

// Material Design Icons
import EyeOff from 'mdi-material-ui/EyeOff';

// Config
import {serviceConfig} from '../../../config/service-config';

const useStyles = makeStyles(theme => ({
    button: {
        margin: theme.spacing(1),
    },
    tooltip: {
        backgroundColor: theme.palette.common.white,
        color: 'rgba(0, 0, 0, 0.87)',
        boxShadow: theme.shadows[1],
        fontSize: 20,
    },
}));

const FORM_KEY = 'CLIENT';

const ClientForm = ({mode, onCancel, onSave}) => {
    const classes = useStyles();
    const {
        formValid,
        formValues,
        formValues: {
            accessTokenValiditySeconds = '',
            clientId = '',
            clientSecret = '',
            redirectUris = [],
            refreshTokenValiditySeconds = '',
        },
        registerForm,
        resetForm,
        setFieldValue,
        submitForm,
    } = useForm(serviceConfig, FORM_KEY, mode);
    const {createToast} = useToast();
    const [masked, setMasked] = useState(true);
    const [action, inputType, Icon] = masked ? ['Show', 'password', RemoveRedEye] : ['Hide', 'text', EyeOff];

    useEffect(() => {
        registerForm();

        return () => resetForm();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleDeleteChip = (key, idx) => {
        setFieldValue(
            key,
            formValues[key].filter((_value, index) => index !== idx)
        );
    };

    const handleSave = () => {
        submitForm(formValues, mode === 'update' ? clientId : undefined).then(({error}) => {
            if (error) {
                createToast(error, {
                    variant: 'error',
                    persist: true,
                    onClose: () => onSave(),
                });
            } else {
                createToast(`Successfully ${mode}d client.`);
                onSave();
            }
        });
    };

    return (
        <>
            <InputTextField
                autoFocus={true}
                disabled={mode !== 'create'}
                fullWidth={true}
                id="clientId"
                label="Client ID"
                margin="dense"
                type="text"
                value={clientId}
                onChange={event => setFieldValue('clientId', event.target.value)}
            />

            <InputTextField
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end">
                            <Tooltip title={`${action} password`}>
                                <IconButton className={classes.eye} onClick={() => setMasked(!masked)}>
                                    <Icon />
                                </IconButton>
                            </Tooltip>
                        </InputAdornment>
                    ),
                }}
                id="clientSecret"
                label="Client Secret"
                margin="dense"
                type={inputType}
                value={clientSecret}
                onChange={event => setFieldValue('clientSecret', event.target.value)}
            />

            <InputTextField
                fullWidth={true}
                id="accessTokenValiditySeconds"
                label="Access Token Validity (Seconds)"
                margin="dense"
                type="text"
                value={accessTokenValiditySeconds}
                onChange={event => setFieldValue('accessTokenValiditySeconds', event.target.value)}
            />

            <InputTextField
                fullWidth={true}
                id="refreshTokenValiditySeconds"
                label="Refresh Token Validity (Seconds)"
                margin="dense"
                type="text"
                value={refreshTokenValiditySeconds}
                onChange={event => setFieldValue('refreshTokenValiditySeconds', event.target.value)}
            />

            <Tooltip
                arrow={true}
                classes={{tooltip: classes.tooltip}}
                disableHoverListener={true}
                placement="left"
                title="Press Enter for each redirect"
            >
                <ChipInput
                    fullWidth={true}
                    id="redirectUris"
                    label="Redirect URIs"
                    value={redirectUris}
                    onAdd={chip => setFieldValue('redirectUris', [...redirectUris, chip])}
                    onDelete={(_, index) => handleDeleteChip('redirectUris', index)}
                />
            </Tooltip>

            <Button
                className={classes.button}
                color="default"
                startIcon={<CancelIcon />}
                variant="contained"
                onClick={onCancel}
            >
                Cancel
            </Button>
            <Button
                className={classes.button}
                color="primary"
                disabled={!formValid}
                startIcon={<SaveIcon />}
                variant="contained"
                onClick={handleSave}
            >
                {mode}
            </Button>
        </>
    );
};

ClientForm.propTypes = {
    mode: PropTypes.string,
    onCancel: PropTypes.func,
    onSave: PropTypes.func,
};

ClientForm.defaultProps = {
    mode: 'create',
};

export default ClientForm;
